import React, { useState, useEffect } from 'react';
import BotonManualHeader from "../../components/BotonManualHeader";
import {
    Container, Row, Col, Card, Form, Button,
    ListGroup, Badge, InputGroup, FormControl, Spinner, Modal, Alert
} from 'react-bootstrap';
import firebaseApp from '../../firebase/credenciales';
import { collection, getDocs, doc, setDoc, updateDoc, deleteDoc, serverTimestamp, getFirestore, query, where } from 'firebase/firestore';
import { BiBuilding, BiSave, BiX, BiEdit } from 'react-icons/bi';
import { FaSearch, FaTrash, FaPlus, FaUsers, FaUserCheck, FaUserTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { logBusinessAction } from '../../services/AuditService';

const db = getFirestore(firebaseApp);

const CentroCostos = ({ usuario }) => {
    // Estados
    const [cecos, setCecos] = useState([]);
    const [rucEmpresa] = useState(usuario.ruc);
    const [filteredCecos, setFilteredCecos] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    // Estado del Formulario
    const [showModal, setShowModal] = useState(false);
    const [mode, setMode] = useState('new'); // 'new' o 'edit'
    const [currentCeco, setCurrentCeco] = useState({
        id: '',
        nombre: '',
        codigo: '',
        descripcion: '',
        activo: true
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Estadísticas
    const [stats, setStats] = useState({
        total: 0,
        withCeco: 0,
        withoutCeco: 0
    });

    // Cargar CECOs al inicializar
    useEffect(() => {
        if (!rucEmpresa) {
            setError("Error: No se pudo determinar la empresa del usuario");
            setLoading(false);
            return;
        }
        fetchCecos();
    }, [rucEmpresa]);

    // Filtrar CECOs por término de búsqueda
    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredCecos(cecos);
        } else {
            const lowercasedSearch = searchTerm.toLowerCase();
            const filtered = cecos.filter(
                c => c.nombre?.toLowerCase().includes(lowercasedSearch) ||
                    c.codigo?.toLowerCase().includes(lowercasedSearch)
            );
            setFilteredCecos(filtered);
        }
    }, [searchTerm, cecos]);

    // Obtener CECOs de Firestore
    const fetchCecos = async () => {
        try {
            setLoading(true);

            // Cargar CECOs
            const ref = collection(db, `cecos/${rucEmpresa}/cecos`);
            const snapshot = await getDocs(ref);

            // Cargar Usuarios para contar por CECO y globales
            const usersRef = collection(db, "users");
            const usersQuery = query(usersRef, where("rucEmpresa", "==", rucEmpresa));
            const usersSnapshot = await getDocs(usersQuery);

            // Calcular conteo de trabajadores por CECO y globales
            const counts = {};
            let withCecoCount = 0;
            const totalEmployees = usersSnapshot.size;

            usersSnapshot.forEach((userDoc) => {
                const userData = userDoc.data();
                const cecoId = userData.cecoId;
                if (cecoId) {
                    counts[cecoId] = (counts[cecoId] || 0) + 1;
                    withCecoCount++;
                }
            });

            setStats({
                total: totalEmployees,
                withCeco: withCecoCount,
                withoutCeco: totalEmployees - withCecoCount
            });

            const cecosData = [];
            snapshot.forEach((doc) => {
                const data = doc.data();
                cecosData.push({
                    id: doc.id,
                    ...data,
                    workerCount: counts[doc.id] || 0
                });
            });

            // Ordenar alfabéticamente por nombre
            cecosData.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));

            setCecos(cecosData);
            setFilteredCecos(cecosData);
            setError(null);
        } catch (error) {
            console.error("Error al cargar Centros de Costo:", error);
            setError("Error al cargar los datos. Por favor, intente nuevamente.");
        } finally {
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setCurrentCeco({
            id: '',
            nombre: '',
            codigo: '',
            descripcion: '',
            activo: true
        });
        setMode('new');
    };

    const handleOpenModal = (ceco = null) => {
        if (ceco) {
            setCurrentCeco(ceco);
            setMode('edit');
        } else {
            setCurrentCeco({
                id: '',
                nombre: '',
                codigo: '',
                descripcion: '',
                activo: true
            });
            setMode('new');
        }
        setShowModal(true);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCurrentCeco({
            ...currentCeco,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    // Guardar CECO
    const saveCeco = async () => {
        if (!currentCeco.nombre.trim()) {
            Swal.fire('Error', 'El nombre del Centro de Costo es obligatorio', 'warning');
            return;
        }

        try {
            setSaving(true);
            const cecosCollectionRef = collection(db, `cecos/${rucEmpresa}/cecos`);

            if (mode === 'new') {
                const newDocRef = doc(cecosCollectionRef);
                await setDoc(newDocRef, {
                    nombre: currentCeco.nombre.trim(),
                    codigo: currentCeco.codigo.trim(),
                    descripcion: currentCeco.descripcion.trim(),
                    activo: currentCeco.activo,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                });
                await logBusinessAction("CREACION_CECO", "GestionUsuarios", { cecoId: newDocRef.id, cecoNombre: currentCeco.nombre.trim() }, usuario);
                Swal.fire('Éxito', 'Centro de Costo creado exitosamente', 'success');
            } else {
                const docRef = doc(db, `cecos/${rucEmpresa}/cecos`, currentCeco.id);
                await updateDoc(docRef, {
                    nombre: currentCeco.nombre.trim(),
                    codigo: currentCeco.codigo.trim(),
                    descripcion: currentCeco.descripcion.trim(),
                    activo: currentCeco.activo,
                    updatedAt: serverTimestamp()
                });
                await logBusinessAction("EDICION_CECO", "GestionUsuarios", { cecoId: currentCeco.id, cecoNombre: currentCeco.nombre.trim() }, usuario);
                Swal.fire('Éxito', 'Centro de Costo actualizado exitosamente', 'success');
            }

            handleCloseModal();
            fetchCecos();
        } catch (error) {
            console.error("Error guardando CECO:", error);
            Swal.fire('Error', 'No se pudo guardar la información', 'error');
        } finally {
            setSaving(false);
        }
    };

    // Eliminar CECO
    const handleDelete = async (id, nombre) => {
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: `Vas a eliminar el Centro de Costo "${nombre}". Esta acción no se puede deshacer.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const docRef = doc(db, `cecos/${rucEmpresa}/cecos`, id);
                await deleteDoc(docRef);
                await logBusinessAction("ELIMINACION_CECO", "GestionUsuarios", { cecoId: id, cecoNombre: nombre }, usuario);
                Swal.fire('Eliminado!', `El Centro de Costo "${nombre}" ha sido eliminado.`, 'success');
                fetchCecos();
            } catch (error) {
                console.error("Error eliminando CECO:", error);
                Swal.fire('Error', 'No se pudo eliminar el Centro de Costo', 'error');
            } finally {
                setLoading(false);
            }
        }
    };

    if (loading && cecos.length === 0) {
        return (
            <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75"
                style={{ zIndex: 1080 }}
            >
                <div className="text-center">
                    <Spinner
                        animation="border"
                        variant="light"
                        style={{ width: "3rem", height: "3rem" }}
                    />
                    <p className="text-white mt-3 mb-0">
                        Cargando Centros de Costo...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-vh-100"
            style={{
                background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
                minHeight: "100vh",
                width: "100vw",
                maxWidth: "100%",
                padding: "20px 0",
            }}
        >
            <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
                {error && (
                    <Alert variant="danger" className="mb-4">
                        {error}
                    </Alert>
                )}
                <Card
                    className="shadow-lg border-0"
                    style={{
                        borderRadius: "15px",
                        overflow: "hidden",
                        minHeight: "90vh",
                    }}
                >
                    {/* Header Premium con degradado azul */}
                    <Card.Header
                        className="text-white py-4 position-relative"
                        style={{
                            background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                            borderBottom: "3px solid #f59e0b",
                        }}
                    >
                        <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
                            <BotonManualHeader />
                        </div>
                        <div className="text-center">
                            <h2 className="fw-bold mb-1">
                                <BiBuilding className="me-3" />
                                Gestión de Centros de Costo (CECO)
                            </h2>
                            <p className="mb-0 opacity-75">
                                Administre los centros de costo para asignar o reportar usuarios
                            </p>
                        </div>
                    </Card.Header>

                    <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
                        {loading && cecos.length > 0 && (
                            <div
                                className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75"
                                style={{ zIndex: 1080 }}
                            >
                                <div className="text-center">
                                    <Spinner
                                        animation="border"
                                        variant="light"
                                        style={{ width: "3rem", height: "3rem" }}
                                    />
                                    <p className="text-white mt-3 mb-0">
                                        Procesando solicitud...
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Barra superior con acciones */}
                        <Card
                            className="shadow-sm border-0 mb-4"
                            style={{ borderRadius: "15px" }}
                        >
                            <Card.Body className="p-4">
                                <Row className="align-items-center">
                                    <Col md={1}>
                                        <div
                                            className="rounded-3 p-3 text-center"
                                            style={{
                                                background:
                                                    "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                                                color: "white",
                                            }}
                                        >
                                            <BiBuilding size={24} />
                                        </div>
                                    </Col>
                                    <Col md={7}>
                                        <h5 className="mb-1 fw-semibold">
                                            Catálogo de CECOs
                                        </h5>
                                        <p className="text-muted mb-0 small">
                                            Cree o modifique los centros de costo de la empresa
                                        </p>
                                    </Col>
                                    <Col md={4} className="text-end">
                                        <Button
                                            variant="light"
                                            onClick={() => handleOpenModal()}
                                            className="d-flex align-items-center gap-2"
                                            style={{
                                                borderRadius: "10px",
                                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                            }}
                                        >
                                            <FaPlus size="16px" />
                                            Nuevo CECO
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Barra de búsqueda */}
                        <Card
                            className="shadow-sm border-0 mb-4"
                            style={{ borderRadius: "15px" }}
                        >
                            <Card.Body className="p-3">
                                <Form onSubmit={(e) => e.preventDefault()}>
                                    <Row className="g-2 align-items-center">
                                        <Col md={12}>
                                            <div className="position-relative">
                                                <FaSearch
                                                    className="position-absolute"
                                                    style={{
                                                        left: "12px",
                                                        top: "50%",
                                                        transform: "translateY(-50%)",
                                                        color: "#6c757d",
                                                        zIndex: 2,
                                                    }}
                                                />
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Buscar por nombre o código del centro de costo..."
                                                    className="form-control-lg ps-5"
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    style={{
                                                        borderRadius: "10px",
                                                        border: "2px solid #e9ecef",
                                                    }}
                                                />
                                            </div>
                                        </Col>
                                    </Row>
                                </Form>
                            </Card.Body>
                        </Card>

                        {/* Indicadores de Estadísticas al estilo Usuarios.js */}
                        <Row className="mb-4 g-3">
                            <Col md={3}>
                                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                                    <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)", color: "white", borderRadius: "15px" }}>
                                        <h4 className="fw-bold mb-1">{stats.total}</h4>
                                        <p className="mb-0 opacity-90">Total Empleados</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                                    <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #059669 0%, #10b981 100%)", color: "white", borderRadius: "15px" }}>
                                        <h4 className="fw-bold mb-1">{stats.withCeco}</h4>
                                        <p className="mb-0 opacity-90">Empleados con CECO</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                                    <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)", color: "white", borderRadius: "15px" }}>
                                        <h4 className="fw-bold mb-1">{stats.withoutCeco}</h4>
                                        <p className="mb-0 opacity-90">Empleados sin CECO</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                                    <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)", color: "white", borderRadius: "15px" }}>
                                        <h4 className="fw-bold mb-1">{cecos.length}</h4>
                                        <p className="mb-0 opacity-90">Total CECOs</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Tabla de CECOs */}
                        <Card
                            className="border-0 shadow-sm mb-4"
                            style={{ borderRadius: "15px" }}
                        >
                            <Card.Header
                                className="text-white py-3"
                                style={{
                                    background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                                    borderRadius: "15px 15px 0 0",
                                }}
                            >
                                <div className="d-flex align-items-center">
                                    <BiBuilding className="me-2" />
                                    <div className="flex-grow-1">
                                        <h5 className="mb-0 fw-semibold">Lista de Centros de Costo</h5>
                                        <small className="opacity-75">
                                            Todos los registros habilitados y deshabilitados
                                        </small>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <Badge bg="light" text="dark" style={{ fontSize: "0.9rem" }}>
                                            {filteredCecos.length} CECOs
                                        </Badge>
                                    </div>
                                </div>
                            </Card.Header>

                            <Card.Body className="p-0">
                                {filteredCecos.length === 0 ? (
                                    <div className="text-center py-5">
                                        <BiBuilding size="4rem" className="text-muted mb-3 opacity-50" />
                                        <h5 className="text-muted">No se encontraron Centros de Costo</h5>
                                        <p className="text-muted small">Cree un nuevo CECO o ajuste su búsqueda.</p>
                                    </div>
                                ) : (
                                    <div style={{ maxHeight: "600px", overflowY: "auto" }}>
                                        <table className="table table-hover align-middle mb-0" style={{ fontSize: "0.9rem" }}>
                                            <thead style={{ backgroundColor: "#f8f9fa", position: "sticky", top: 0, zIndex: 10 }}>
                                                <tr>
                                                    <th className="py-3 px-3 border-0 text-secondary fw-semibold text-center" style={{ width: "10%" }}>Código</th>
                                                    <th className="py-3 px-3 border-0 text-secondary fw-semibold text-center" style={{ width: "30%" }}>Nombre del CECO</th>
                                                    <th className="py-3 px-3 border-0 text-secondary fw-semibold text-center" style={{ width: "30%" }}>Descripción</th>
                                                    <th className="py-3 px-3 border-0 text-secondary fw-semibold text-center" style={{ width: "10%" }}>Trabajadores</th>
                                                    <th className="py-3 px-3 border-0 text-secondary fw-semibold text-center" style={{ width: "10%" }}>Estado</th>
                                                    <th className="py-3 px-3 border-0 text-secondary fw-semibold text-center" style={{ width: "20%" }}>Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredCecos.map((ceco) => (
                                                    <tr key={ceco.id}>
                                                        <td className="py-3 px-3 text-center">
                                                            <span className="fw-medium text-dark">{ceco.codigo || '-'}</span>
                                                        </td>
                                                        <td className="py-3 px-3 text-center">
                                                            <span className="fw-bold text-dark">{ceco.nombre}</span>
                                                        </td>
                                                        <td className="py-3 px-3 text-center text-muted">
                                                            {ceco.descripcion || '-'}
                                                        </td>
                                                        <td className="py-3 px-3 text-center">
                                                            <Badge bg={ceco.workerCount > 0 ? "primary" : "light"} text={ceco.workerCount > 0 ? "white" : "dark"} style={{ fontSize: "0.85rem" }}>
                                                                {ceco.workerCount}
                                                            </Badge>
                                                        </td>
                                                        <td className="py-3 px-3 text-center">
                                                            {ceco.activo !== false ? (
                                                                <Badge bg="success" pill style={{ fontSize: "0.8rem" }}>Activo</Badge>
                                                            ) : (
                                                                <Badge bg="secondary" pill style={{ fontSize: "0.8rem" }}>Inactivo</Badge>
                                                            )}
                                                        </td>
                                                        <td className="py-3 px-3 text-center">
                                                            <div className="d-flex gap-1 justify-content-center">
                                                                <Button
                                                                    variant="outline-warning"
                                                                    size="sm"
                                                                    onClick={() => handleOpenModal(ceco)}
                                                                    style={{ borderRadius: "6px", padding: "4px 8px" }}
                                                                    title="Editar"
                                                                >
                                                                    <BiEdit size="14px" />
                                                                </Button>
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={() => handleDelete(ceco.id, ceco.nombre)}
                                                                    style={{ borderRadius: "6px", padding: "4px 8px" }}
                                                                    title="Eliminar"
                                                                >
                                                                    <FaTrash size="14px" />
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Card.Body>
                </Card>

                {/* Modal para Crear / Editar CECO */}
                <Modal show={showModal} onHide={handleCloseModal} centered>
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="fw-bold text-dark d-flex align-items-center">
                            <BiBuilding size="1.8rem" className="me-2 text-primary" />
                            {mode === 'new' ? 'Nuevo Centro de Costo' : 'Editar Centro de Costo'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-3">
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">Nombre del CECO <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    name="nombre"
                                    value={currentCeco.nombre}
                                    onChange={handleInputChange}
                                    placeholder="Ej: Operaciones Zona Norte"
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">Código</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="codigo"
                                    value={currentCeco.codigo}
                                    onChange={handleInputChange}
                                    placeholder="Ej: OZN-001"
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">Descripción</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="descripcion"
                                    value={currentCeco.descripcion}
                                    onChange={handleInputChange}
                                    placeholder="Breve descripción del centro de costo..."
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="switch"
                                    id="activo-switch"
                                    label={currentCeco.activo ? 'CECO Activo' : 'CECO Inactivo'}
                                    name="activo"
                                    checked={currentCeco.activo}
                                    onChange={handleInputChange}
                                    className="fw-medium text-secondary"
                                />
                                <Form.Text className="text-muted ms-5">
                                    Los CECOs inactivos no aparecerán en las listas para asignar nuevos usuarios.
                                </Form.Text>
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0">
                        <Button variant="light" onClick={handleCloseModal} disabled={saving} style={{ borderRadius: '8px' }}>
                            <BiX size="1.2rem" className="me-1" />
                            Cancelar
                        </Button>
                        <Button variant="primary" onClick={saveCeco} disabled={saving} style={{ borderRadius: '8px', background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)", border: "none" }}>
                            {saving ? (
                                <><Spinner size="sm" className="me-2" /> Guardando...</>
                            ) : (
                                <><BiSave size="1.2rem" className="me-1" /> Guardar CECO</>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Container>
        </div>
    );
};

export default CentroCostos;
